import React, { useEffect, useState, useRef } from "react";

const SeamlessCarousel = ({ children, interval = 3000 }) => {
  const slides = React.Children.toArray(children);
  const length = slides.length;
  const displaySlides = [...slides, ...slides, ...slides];

  const [currentIndex, setCurrentIndex] = useState(length);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  
  // New states for responsive count and dragging
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  // 1. Logic to determine items per view (1 on mobile, 2 tablet, 3 desktop)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 1024) setItemsPerView(2);
      else setItemsPerView(3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-play Logic
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!isPaused && !isDragging) {
      timeoutRef.current = setTimeout(() => {
        handleNext();
      }, interval);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, interval, isPaused, isDragging]);

  const handleNext = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= length * 2) {
      setIsTransitioning(false);
      setCurrentIndex(length);
    } else if (currentIndex <= length - 1) {
      setIsTransitioning(false);
      setCurrentIndex(length * 2 - 1);
    }
  };

  // Dragging Logic (Mouse & Touch)
  const onStart = (e) => {
    setIsPaused(true);
    setIsDragging(true);
    setIsTransitioning(false);
    setStartX(e.pageX || e.touches[0].pageX);
  };

  const onMove = (e) => {
    if (!isDragging) return;
    const currentX = e.pageX || (e.touches ? e.touches[0].pageX : 0);
    setDragOffset(currentX - startX);
  };

  const onEnd = () => {
    if (!isDragging) return;
    const threshold = 50; // pixels moved to trigger slide change
    setIsTransitioning(true);
    
    if (dragOffset < -threshold) handleNext();
    else if (dragOffset > threshold) handlePrev();
    
    setIsDragging(false);
    setDragOffset(0);
  };

  useEffect(() => {
    if (!isTransitioning) {
      const raf = requestAnimationFrame(() => setIsTransitioning(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [isTransitioning]);

  return (
    <div 
      className="relative group w-full mx-auto overflow-hidden touch-pan-y select-none" 
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => !isDragging && setIsPaused(false)}
    >
      {/* Navigation Buttons */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white bg-blue-600 w-12 h-12 hidden md:flex items-center justify-center rounded-full shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-800"
      >
        <span className="text-2xl font-bold">←</span>
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white bg-blue-600 w-12 h-12 hidden md:flex items-center justify-center rounded-full shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-800"
      >
        <span className="text-2xl font-bold">→</span>
      </button>

      {/* Slider Container */}
      <div 
        ref={containerRef}
        className="overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={onStart}
        onMouseMove={onMove}
        onMouseUp={onEnd}
        onMouseLeave={onEnd}
        onTouchStart={onStart}
        onTouchMove={onMove}
        onTouchEnd={onEnd}
      >
        <div
          className="flex"
          onTransitionEnd={handleTransitionEnd}
          style={{
            // Dynamic width calculation based on itemsPerView + manual drag offset
            transform: `translateX(calc(-${currentIndex * (100 / itemsPerView)}% + ${dragOffset}px))`,
            transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
          }}
        >
          {displaySlides.map((slide, i) => (
            <div 
              key={i} 
              className="w-full sm:w-1/2 lg:w-1/3 flex-shrink-0 px-3 py-4"
            >
              <div className="h-full w-full pointer-events-auto">
                {/* To keep "Learn More" buttons clickable, ensure images/containers have draggable="false" */}
                {React.cloneElement(slide, { draggable: false })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeamlessCarousel;